import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Observable, Subscription, take, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeedbackService } from '../services/feedback.service';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { FEEDBACK_ROTATION_INTERVAL } from '@app/utils/constants/feedback.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { Feedback } from '@app/utils/interfaces/feedback.interface';

@Component({
  selector: 'app-feedback-list',
  standalone: true,
  imports: [CommonModule, IconComponent, TranslateModule],
  templateUrl: './feedback-list.component.html',
  styleUrl: './feedback-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly feedbackService = inject(FeedbackService);
  private readonly platformId = inject(PLATFORM_ID);

  public feedbacks$: Observable<Feedback[]> = this.feedbackService.getFeedbacks();
  public currentIndex: WritableSignal<number> = signal(0);
  public iconName: typeof IconName = IconName;
  private feedbacksLength = 0;

  private autoRotateSub?: Subscription;

  public ngOnInit(): void {
    this.feedbacks$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((feedbacks) => {
      this.feedbacksLength = feedbacks.length;
      this.startAutoRotation();
    });
  }

  private startAutoRotation(): void {
    this.autoRotateSub?.unsubscribe();

    if (this.feedbacksLength && isPlatformBrowser(this.platformId)) {
      this.autoRotateSub = timer(FEEDBACK_ROTATION_INTERVAL, FEEDBACK_ROTATION_INTERVAL)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.nextFeedbackInternal());
    }
  }

  private nextFeedbackInternal(): void {
    if (this.feedbacksLength) {
      this.currentIndex.update((index) => (index + 1) % this.feedbacksLength);
    }
  }

  public nextFeedback(feedbacksLength: number): void {
    if (feedbacksLength) {
      this.currentIndex.update((index) => (index + 1) % feedbacksLength);
      this.restartAutoRotation();
    }
  }

  public prevFeedback(feedbacksLength: number): void {
    if (feedbacksLength) {
      this.currentIndex.update((index) => (index - 1 + feedbacksLength) % feedbacksLength);
      this.restartAutoRotation();
    }
  }

  private restartAutoRotation(): void {
    this.startAutoRotation();
  }
}
